import type { InputHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';
import {
  controlBorderClass,
  controlDisabled,
  controlFocus,
  controlHover,
  controlLoading,
  controlSurface,
  errorMessageId,
} from '../primitives/shared.js';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  loading?: boolean;
}

/** PRM-03 Text input — single-line entry. Labelled by PRM-11, never placeholder-only. */
export function Input({
  label,
  error,
  loading,
  id,
  className,
  disabled,
  required,
  'aria-describedby': ariaDescribedBy,
  ...props
}: InputProps) {
  const inputId = id ?? props.name;
  const errorId = errorMessageId(inputId);
  const describedBy =
    [ariaDescribedBy, error ? errorId : undefined].filter(Boolean).join(' ') || undefined;

  const control = (
    <>
      <input
        id={inputId}
        data-primitive="PRM-03"
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        aria-disabled={disabled || undefined}
        aria-busy={loading || undefined}
        disabled={disabled}
        required={required}
        className={cn(
          controlSurface,
          'min-h-control',
          controlBorderClass(error),
          controlHover,
          controlFocus,
          controlDisabled,
          controlLoading,
          'disabled:cursor-not-allowed disabled:bg-off disabled:text-disabled',
          className,
        )}
        {...props}
      />
      {error ? (
        <span id={errorId} role="alert" className="text-body-sm text-danger">
          {error}
        </span>
      ) : null}
    </>
  );

  // Only wrap in <label> for standalone labelled use. When nested in FormField
  // (no label prop), a plain div avoids a second LabelText in the a11y tree.
  if (label) {
    return (
      <label htmlFor={inputId} className="flex flex-col gap-field-label">
        <span className="text-label font-medium text-default">
          {label}
          {required ? <span className="sr-only"> (required)</span> : null}
        </span>
        {control}
      </label>
    );
  }

  return <div className="flex flex-col gap-field-label">{control}</div>;
}
